import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  reengagementApi,
  type ReengagementCandidate,
  type ReengagementContactChannel,
  type ReengagementListStatus,
  type ReengagementUpdate,
} from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, ConfirmDialog, LoadingBlock, Pagination, Select, TextInput } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { todayInTimezone } from '../components/schedule/format.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { addDaysToDate } from '@shared/calculations/recurrence.ts';

const PAGE_SIZE = 20;

type LaunchChannel = 'call' | 'sms' | 'email';
type WorkflowDialog = {
  type: 'snooze' | 'dismiss';
  candidate: ReengagementCandidate;
} | null;

/**
 * A calendar date this many days from today, read the way the person at the
 * screen reads a calendar.
 *
 * Both halves matter and both used to be wrong together: adding the days to a
 * local `Date` and then slicing `toISOString()` reads the UTC day, so at
 * UTC-7 a 6pm "snooze 7 days" produced tomorrow-plus-seven — eight days — and
 * the row then said so. `todayInTimezone` formats the local day, and
 * `addDaysToDate` walks calendar days rather than instants.
 */
function isoDateAfter(days: number): string {
  return addDaysToDate(todayInTimezone(), days);
}

/**
 * The earliest day the picker may offer.
 *
 * Tomorrow, because `dateToIso` below turns a chosen day into 09:00 that
 * morning and refuses an instant already past: any day it would accept has to
 * still have its 09:00 ahead of it, and today only does before nine. The two
 * are written next to each other so they cannot disagree again — offering a
 * day that cannot then be confirmed is how the picker read after the UTC fix
 * made today selectable at six in the evening.
 *
 * Tomorrow rather than a later hour today because this feature is written in
 * whole days: the presets are "7 days" and "30 days", the field reads "Show
 * again on", the row afterwards reads "Snoozed until <date>", and there is no
 * clock anywhere in the dialog. "Show again on today" is not a snooze anybody
 * asked for — it is the row they are already looking at.
 */
function earliestSnoozeDate(): string {
  return isoDateAfter(1);
}

function dateToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T09:00:00`);
  return Number.isNaN(date.getTime()) || date.getTime() <= Date.now() ? null : date.toISOString();
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getDateLocale(), {
    dateStyle: 'medium',
  }).format(date);
}

function composerHref(candidate: ReengagementCandidate, channel: LaunchChannel): string | null {
  if (channel === 'call') return candidate.phone ? `tel:${candidate.phone}` : null;
  if (channel === 'sms') {
    return candidate.phone ? `sms:${candidate.phone}?body=${encodeURIComponent(candidate.outreach.smsBody)}` : null;
  }
  if (!candidate.email) return null;
  const query = new URLSearchParams({
    subject: candidate.outreach.emailSubject,
    body: candidate.outreach.emailBody,
  });
  return `mailto:${candidate.email}?${query.toString()}`;
}

function channelReasonKey(reason: string | null): string {
  switch (reason) {
    case 'missing_phone':
    case 'no_phone':
      return 'missingPhone';
    case 'missing_email':
    case 'no_email':
      return 'missingEmail';
    case 'sms_marketing_disabled':
    case 'marketing_consent_required':
    case 'no_consent':
      return 'noConsent';
    case 'landline':
      return 'landline';
    case 'do_not_contact':
      return 'doNotContact';
    case 'plan_required':
      return 'planRequired';
    case 'sms_unavailable':
    case 'provider_unavailable':
      return 'smsUnavailable';
    default:
      return 'unavailable';
  }
}

export default function CustomerFollowUpsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('reengagement.documentTitle'));
  const { can } = useSession();
  const queryClient = useQueryClient();
  const canRead = can('customers:read');
  const canWrite = can('customers:write');

  const [status, setStatus] = useState<ReengagementListStatus>('open');
  const [page, setPage] = useState(1);
  const [launched, setLaunched] = useState<{
    id: string;
    channel: LaunchChannel;
  } | null>(null);
  const [dialog, setDialog] = useState<WorkflowDialog>(null);
  const [snoozeChoice, setSnoozeChoice] = useState<'7' | '30' | 'custom'>('7');
  const [customSnoozeDate, setCustomSnoozeDate] = useState(isoDateAfter(7));
  const [dismissReason, setDismissReason] = useState<'do_not_contact' | 'bad_contact' | 'not_interested' | 'other'>(
    'not_interested',
  );
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['reengagement', 'list', { status, page }],
    queryFn: () => reengagementApi.list({ status, page, pageSize: PAGE_SIZE }),
    enabled: canRead,
    // Run and fail visibly while offline. The API method is getFresh, so a
    // stale persisted queue can never masquerade as the current work list.
    networkMode: 'always',
    staleTime: 0,
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReengagementUpdate }) => reengagementApi.update(id, body),
    networkMode: 'always',
    onSuccess: async () => {
      setActionError(null);
      setDialog(null);
      setLaunched(null);
      // Keep the mutation pending until the live list and summary have
      // finished replacing the candidate that was just acted on.
      await queryClient.invalidateQueries({ queryKey: ['reengagement'] });
    },
    onError: (caught) => setActionError(caught instanceof Error ? caught.message : t('reengagement.actionError')),
  });

  // React Query may have an older in-memory answer for this key, and a
  // status/page switch used to keep the previous page as placeholder data.
  // Neither is safe to act on: another device may already have contacted or
  // dismissed the customer. Only expose the queue after the live request has
  // settled successfully, and hide it again while a mutation refetches.
  const livePage = !query.isFetching && query.error === null && !update.isPending ? query.data : undefined;

  const summary = livePage?.summary;
  const statuses = useMemo(
    () =>
      (['open', 'snoozed', 'contacted', 'dismissed'] as const).map((key) => ({
        key,
        count: summary?.[key],
      })),
    [summary],
  );

  function chooseStatus(next: ReengagementListStatus) {
    setStatus(next);
    setPage(1);
    setLaunched(null);
  }

  function openSnooze(candidate: ReengagementCandidate) {
    setActionError(null);
    setSnoozeChoice('7');
    setCustomSnoozeDate(isoDateAfter(7));
    setDialog({ type: 'snooze', candidate });
  }

  function openDismiss(candidate: ReengagementCandidate) {
    setActionError(null);
    setDismissReason('not_interested');
    setDialog({ type: 'dismiss', candidate });
  }

  function confirmSnooze() {
    if (!dialog || dialog.type !== 'snooze') return;
    const date = snoozeChoice === 'custom' ? customSnoozeDate : isoDateAfter(Number(snoozeChoice));
    const until = dateToIso(date);
    if (!until) {
      setActionError(t('reengagement.snoozeInvalid'));
      return;
    }
    update.mutate({
      id: dialog.candidate.id,
      body: { action: 'snooze', until },
    });
  }

  if (!canRead) {
    return (
      <WorkPage title={t('reengagement.title')} description={t('reengagement.description')}>
        <Alert tone="error" title={t('reengagement.noAccessTitle')}>
          {t('reengagement.noAccessBody')}
        </Alert>
      </WorkPage>
    );
  }

  return (
    <WorkPage
      title={t('reengagement.title')}
      description={t('reengagement.description')}
      actions={
        <Link className="btn btn-secondary" to="/customers">
          {t('reengagement.backToCustomers')}
        </Link>
      }
    >
      <section aria-label={t('reengagement.summaryAria')} className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statuses.map(({ key, count }) => (
          <button
            key={key}
            type="button"
            aria-pressed={status === key}
            className={`rounded-lg border bg-white p-4 text-left transition hover:border-[color:var(--color-brand)] ${
              status === key
                ? 'border-[color:var(--color-brand)] ring-2 ring-[color:var(--color-brand-soft)]'
                : 'border-[color:var(--color-line)]'
            }`}
            onClick={() => chooseStatus(key)}
          >
            <span className="block text-2xl font-bold">{count ?? '—'}</span>
            <span className="text-sm text-[color:var(--color-muted)]">{t(`reengagement.status.${key}`)}</span>
          </button>
        ))}
      </section>

      <div className="card mb-6">
        <div className="max-w-xs">
          <Select
            label={t('reengagement.filterLabel')}
            value={status}
            options={(['open', 'snoozed', 'contacted', 'dismissed', 'all'] as const).map((value) => ({
              value,
              label: t(`reengagement.status.${value}`),
            }))}
            onChange={(event) => chooseStatus(event.target.value as ReengagementListStatus)}
          />
        </div>
        <p className="text-sm text-[color:var(--color-muted)]">{t('reengagement.liveDataHint')}</p>
      </div>

      {query.isFetching || update.isPending ? <LoadingBlock label={t('reengagement.loading')} /> : null}

      {!query.isFetching && query.error !== null ? (
        <Alert tone="error" title={t('reengagement.loadErrorTitle')}>
          <p>{t('reengagement.loadErrorBody')}</p>
          <Button className="mt-3" onClick={() => void query.refetch()}>
            {t('reengagement.tryAgain')}
          </Button>
        </Alert>
      ) : null}

      {livePage && livePage.items.length === 0 ? (
        <WorkEmpty title={t('reengagement.emptyTitle')}>
          <p>{t(status === 'open' ? 'reengagement.emptyOpen' : 'reengagement.emptyFiltered')}</p>
        </WorkEmpty>
      ) : null}

      {livePage && livePage.items.length > 0 ? (
        <div className="space-y-4">
          {livePage.items.map((candidate) => {
            const cardId = `reengagement-${candidate.id}`;
            const mutable = candidate.status === 'open' || candidate.status === 'snoozed';
            const lastClean = formatDate(candidate.lastCompletedAt);
            const latestCancellation = formatDate(candidate.latestCancellationAt);
            const snoozedUntil = formatDate(candidate.snoozedUntil);

            return (
              <article key={candidate.id} className="card" aria-labelledby={`${cardId}-name`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <Link
                      id={`${cardId}-name`}
                      className="text-lg font-semibold text-[color:var(--color-brand)] hover:underline"
                      to={`/customers/${candidate.customerId}`}
                    >
                      {candidate.customerName}
                    </Link>
                    <p className="mt-1 text-sm text-[color:var(--color-muted)]">
                      {[candidate.email, candidate.phone].filter(Boolean).join(' · ') || t('reengagement.noContact')}
                    </p>
                  </div>
                  <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                    {t(`reengagement.status.${candidate.status}`)}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {candidate.reasons.map((reason) => (
                    <span
                      key={reason}
                      className="badge border-[color:var(--color-warning)] bg-[color:var(--color-warning-soft)] text-[color:var(--color-ink)]"
                    >
                      {reason === 'inactive'
                        ? candidate.daysSinceLastClean === null
                          ? t('reengagement.reason.inactive')
                          : t('reengagement.reason.inactiveDays', {
                              count: candidate.daysSinceLastClean,
                            })
                        : t('reengagement.reason.repeatedCancellations', {
                            count: candidate.cancellationCount,
                            days: candidate.cancellationWindowDays,
                          })}
                    </span>
                  ))}
                </div>

                <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-[color:var(--color-muted)]">{t('reengagement.lastClean')}</dt>
                    <dd className="font-medium">{lastClean ?? t('reengagement.notAvailable')}</dd>
                  </div>
                  <div>
                    <dt className="text-[color:var(--color-muted)]">{t('reengagement.latestCancellation')}</dt>
                    <dd className="font-medium">{latestCancellation ?? t('reengagement.none')}</dd>
                  </div>
                  <div>
                    <dt className="text-[color:var(--color-muted)]">{t('reengagement.followUpState')}</dt>
                    <dd className="font-medium">
                      {candidate.status === 'snoozed' && snoozedUntil
                        ? t('reengagement.snoozedUntil', { date: snoozedUntil })
                        : t(`reengagement.status.${candidate.status}`)}
                    </dd>
                  </div>
                </dl>

                {mutable && canWrite ? (
                  <div className="mt-5 border-t border-[color:var(--color-line)] pt-4">
                    <h3 className="text-sm font-semibold">{t('reengagement.reachOutHeading')}</h3>
                    <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('reengagement.reachOutHint')}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(['call', 'sms', 'email'] as const).map((channel) => {
                        const availability = candidate.channels[channel];
                        const href = composerHref(candidate, channel);
                        const allowed = availability.allowed && href !== null;
                        const reasonId = `${cardId}-${channel}-reason`;

                        return allowed ? (
                          <a
                            key={channel}
                            className="btn btn-secondary"
                            href={href}
                            onClick={() => {
                              setActionError(null);
                              setLaunched({ id: candidate.id, channel });
                            }}
                          >
                            {t(`reengagement.channel.${channel}`)}
                          </a>
                        ) : (
                          <span key={channel} className="inline-flex flex-col">
                            <button type="button" className="btn btn-secondary" disabled aria-describedby={reasonId}>
                              {t(`reengagement.channel.${channel}`)}
                            </button>
                            <span id={reasonId} className="mt-1 max-w-40 text-xs text-[color:var(--color-muted)]">
                              {t(`reengagement.channelReason.${channelReasonKey(availability.reason)}`)}
                            </span>
                          </span>
                        );
                      })}
                    </div>

                    {launched?.id === candidate.id ? (
                      <div
                        className="mt-4 rounded-lg border border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] p-3"
                        role="status"
                      >
                        <p className="text-sm">{t('reengagement.composerOpened')}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            variant="primary"
                            busy={update.isPending}
                            onClick={() => {
                              const channel: ReengagementContactChannel =
                                launched.channel === 'call' ? 'phone' : launched.channel;
                              update.mutate({
                                id: candidate.id,
                                body: { action: 'contacted', channel },
                              });
                            }}
                          >
                            {t('reengagement.markContacted', {
                              channel: t(`reengagement.channel.${launched.channel}`).toLowerCase(),
                            })}
                          </Button>
                          <Button onClick={() => setLaunched(null)}>{t('reengagement.notYet')}</Button>
                        </div>
                        {actionError ? (
                          <p className="mt-2 text-sm text-[color:var(--color-danger)]">{actionError}</p>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button onClick={() => openSnooze(candidate)}>{t('reengagement.snooze')}</Button>
                      <Button variant="ghost" onClick={() => openDismiss(candidate)}>
                        {t('reengagement.dismiss')}
                      </Button>
                      <Link className="btn btn-ghost" to={`/schedule/new?customerId=${candidate.customerId}`}>
                        {t('reengagement.scheduleClean')}
                      </Link>
                    </div>
                  </div>
                ) : !canWrite && mutable ? (
                  <p className="mt-4 border-t border-[color:var(--color-line)] pt-4 text-sm text-[color:var(--color-muted)]">
                    {t('reengagement.readOnlyHint')}
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}

      <Pagination page={page} totalPages={livePage?.totalPages} onPageChange={setPage} />

      {dialog?.type === 'snooze' ? (
        <ConfirmDialog
          title={t('reengagement.snoozeTitle', {
            name: dialog.candidate.customerName,
          })}
          confirmLabel={t('reengagement.snoozeConfirm')}
          busy={update.isPending}
          error={actionError}
          onCancel={() => setDialog(null)}
          onConfirm={confirmSnooze}
        >
          <Select
            label={t('reengagement.snoozeFor')}
            value={snoozeChoice}
            options={[
              { value: '7', label: t('reengagement.snoozeSeven') },
              { value: '30', label: t('reengagement.snoozeThirty') },
              { value: 'custom', label: t('reengagement.snoozeCustom') },
            ]}
            onChange={(event) => setSnoozeChoice(event.target.value as '7' | '30' | 'custom')}
          />
          {snoozeChoice === 'custom' ? (
            <TextInput
              label={t('reengagement.snoozeDate')}
              type="date"
              min={earliestSnoozeDate()}
              value={customSnoozeDate}
              onChange={(event) => setCustomSnoozeDate(event.target.value)}
            />
          ) : null}
        </ConfirmDialog>
      ) : null}

      {dialog?.type === 'dismiss' ? (
        <ConfirmDialog
          title={t('reengagement.dismissTitle', {
            name: dialog.candidate.customerName,
          })}
          confirmLabel={t('reengagement.dismissConfirm')}
          confirmVariant="danger"
          busy={update.isPending}
          error={actionError}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            update.mutate({
              id: dialog.candidate.id,
              body: { action: 'dismiss', reason: dismissReason },
            })
          }
        >
          <Select
            label={t('reengagement.dismissReason')}
            value={dismissReason}
            options={(['do_not_contact', 'bad_contact', 'not_interested', 'other'] as const).map((value) => ({
              value,
              label: t(`reengagement.dismissReasons.${value}`),
            }))}
            onChange={(event) => setDismissReason(event.target.value as typeof dismissReason)}
          />
        </ConfirmDialog>
      ) : null}
    </WorkPage>
  );
}
